/**
 * EVChargerLayer.tsx — EV charging station layer (MAP-03 + T-012/W2)
 *
 * Independent layer module: owns its own source/layer lifecycle and fetches
 * /api/ev-stations/nearby when an active location is set. Renders empty until a
 * search result provides a location.
 *
 * W2 upgrades (T-012):
 *  - Native MapLibre clustering (cluster:true) with a cluster chip + count badge (T-214;
 *    originally a plain teal bubble), and unclustered single-station circles.
 *  - Circles are color-coded by the highest charger level present
 *    (DC Fast → green, Level 2 → blue, else Level 1 → slate) and sized by total ports.
 *  - Clicking a station opens a popup with name / network / address / per-level ports
 *    and connector-type chips. Clicking a cluster zooms to expand it.
 *  - A `visible` prop toggles the whole layer set (same pattern as TractLayer).
 *  - Fetch radius is zoom-aware: zoomed out fetches a wider radius so the cluster
 *    stays populated; re-fetches on zoomend when the radius tier changes.
 *
 * T-214 — charging-station icon: single stations render a Maki charging-station chip
 * (poiMarkers.ts, the same pipeline as the POI icons), tinted by charger level, on a
 * symbol layer ABOVE the circle. The circle stays as the port-count-sized colored halo,
 * so the size channel is preserved; clusters keep the teal bubble + count.
 *
 * The AFDC response is normalised server-side (evStations.ts) to
 * { count, stations: [{ id, name, address, lat, lng, network, connectorTypes,
 *   portsLevel1, portsLevel2, portsDcFast, ... }] }; this component converts it
 * to clustered GeoJSON for the MapLibre source.
 */
import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import type { GeoJSONSource, MapLayerMouseEvent } from 'maplibre-gl'
import { useMap } from './MapContext'
import { openMarkerPopup, closeMarkerPopup } from './markerPopup'
import { toast } from '../ui/toaster'
import { authedFetch } from '../../lib/api'
import { isMapAlive } from '../../lib/mapLiveness'
import {
  evClusterImageId,
  evIconImageExpression,
  registerEvMarkerImages,
  removeEvMarkerImages,
} from './poiMarkers'
// T-214: align EV clustering depth to the accepted POI baseline so power stations collapse
// over the same zoom band as the POI overlay (Ricardo's "same clustering solution" ask).
import { CLUSTER_MAX_ZOOM } from './POILayerMgr'

export interface EVChargerLayerProps {
  location: { lat: number; lng: number } | null
  visible?: boolean
  /** Called when a single station is clicked — lets the parent move the pin / fetch scores. */
  onStationSelect?: (loc: { lat: number; lng: number }) => void
}

const SOURCE_ID = 'ev-chargers'
const LAYER_CLUSTER_ID = 'ev-clusters'
const LAYER_COUNT_ID = 'ev-cluster-count'
const LAYER_POINT_ID = 'ev-chargers-circle'
// T-214: charging-station glyph over the circle (single stations only).
const LAYER_ICON_ID = 'ev-chargers-icon'
const ALL_LAYER_IDS = [LAYER_CLUSTER_ID, LAYER_COUNT_ID, LAYER_POINT_ID, LAYER_ICON_ID]

// Normalised shape returned by GET /api/ev-stations/nearby.
interface NormalizedStation {
  id?: number
  name?: string
  address?: string
  lat: number
  lng: number
  network?: string | null
  connectorTypes?: string[]
  portsLevel1?: number | null
  portsLevel2?: number | null
  portsDcFast?: number | null
}

// Zoom-aware fetch radius (miles): wider when zoomed out so clusters stay populated.
function radiusMiForZoom(zoom: number): number {
  if (zoom >= 13) return 5
  if (zoom >= 11) return 15
  if (zoom >= 9) return 40
  return 100
}

// Minimal HTML escaping for popup interpolation (station data is external).
function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function stationsToGeoJSON(stations: NormalizedStation[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: stations
      .filter((s) => typeof s.lat === 'number' && typeof s.lng === 'number')
      .map((s) => {
        const l1 = Number(s.portsLevel1 ?? 0)
        const l2 = Number(s.portsLevel2 ?? 0)
        const dc = Number(s.portsDcFast ?? 0)
        // T-214: same DC → L2 → L1 precedence as the circle-color paint, exposed as a
        // discrete prop so the icon layer can match the level-tinted charging chip.
        const level = dc > 0 ? 'dc' : l2 > 0 ? 'l2' : 'l1'
        return {
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [s.lng, s.lat] },
          properties: {
            id: s.id ?? null,
            name: s.name ?? 'EV Station',
            address: s.address ?? '',
            network: s.network ?? '',
            // GeoJSON props are read back as strings by MapLibre — store as JSON.
            connectorTypes: JSON.stringify(s.connectorTypes ?? []),
            portsLevel1: l1,
            portsLevel2: l2,
            portsDcFast: dc,
            totalPorts: l1 + l2 + dc,
            level,
          },
        }
      }),
  }
}

/**
 * How many stations a cluster popup will list. A cluster can hold more than is sensible to
 * render, so the list is capped — and the header SAYS SO when it truncates, rather than
 * passing a prefix off as the whole set. The list scrolls, so the cap is the only limit.
 * (Mirrors CLUSTER_LEAVES_LIMIT on the POI layer.)
 */
export const EV_CLUSTER_LEAVES_LIMIT = 100

/** One line per station in a cluster: name + the ports that matter for siting. */
function openStationLeavesPopup(
  map: maplibregl.Map,
  lngLat: maplibregl.LngLatLike,
  leaves: Array<{ properties?: Record<string, unknown> | null }>,
  totalCount?: number,
): void {
  const items = leaves
    .map((l) => {
      const p = l.properties ?? {}
      const dc = Number(p['portsDcFast'] ?? 0)
      const l2 = Number(p['portsLevel2'] ?? 0)
      const ports = [
        dc > 0 ? `<span style="color:var(--ev-dc);font-weight:600">⚡ ${dc} DC</span>` : '',
        l2 > 0 ? `<span style="color:var(--ev-l2);font-weight:600">🔌 ${l2} L2</span>` : '',
      ]
        .filter(Boolean)
        .join(' · ')
      return `<li style="margin:3px 0;list-style:none">
        <span style="font-weight:600">${esc(p['name'] ?? 'EV Station')}</span>
        ${ports ? `<div style="font-size:10px;margin-top:1px">${ports}</div>` : ''}
      </li>`
    })
    .join('')
  const total = totalCount ?? leaves.length
  const truncated = total > leaves.length
  openMarkerPopup(map, {
    lngLat,
    maxWidth: '260px',
    offset: 10,
    html: `<div style="font-family:Inter,system-ui,sans-serif;font-size:12px">
        <p style="font-weight:700;margin:0 0 4px">⚡ ${total} charging stations</p>
        ${truncated ? `<p style="color:var(--muted-foreground);margin:0 0 4px;font-size:10px">showing the first ${leaves.length}</p>` : ''}
        <ul style="margin:0;padding:0;max-height:220px;overflow-y:auto">${items}</ul>
      </div>`,
  })
}

function stationPopupHTML(p: Record<string, unknown>): string {
  let connTypes: string[] = []
  try {
    connTypes = JSON.parse(String(p['connectorTypes'] ?? '[]')) as string[]
  } catch {
    connTypes = []
  }
  const chips = connTypes
    .map(
      (t) =>
        `<span style="display:inline-block;background:var(--border);color:var(--foreground);border-radius:4px;padding:1px 6px;margin:2px 2px 0 0;font-size:10px">${esc(t)}</span>`,
    )
    .join('')
  const dc = Number(p['portsDcFast'] ?? 0)
  const l2 = Number(p['portsLevel2'] ?? 0)
  const l1 = Number(p['portsLevel1'] ?? 0)
  return `
    <div style="font-family:Inter,system-ui,sans-serif;font-size:12px">
      <p style="font-weight:700;margin:0">⚡ ${esc(p['name'] ?? 'EV Station')}</p>
      ${p['network'] ? `<p style="color:var(--muted-foreground);margin:2px 0;font-size:11px">${esc(p['network'])}</p>` : ''}
      ${p['address'] ? `<p style="color:var(--muted-foreground);margin:2px 0;font-size:10px">${esc(p['address'])}</p>` : ''}
      <div style="margin-top:6px;font-size:11px">
        ${dc > 0 ? `<div style="color:var(--ev-dc);font-weight:600">⚡ ${dc} DC Fast</div>` : ''}
        ${l2 > 0 ? `<div style="color:var(--ev-l2);font-weight:600">🔌 ${l2} Level 2</div>` : ''}
        ${l1 > 0 ? `<div style="color:var(--muted-foreground)">🔌 ${l1} Level 1</div>` : ''}
      </div>
      ${chips ? `<div style="margin-top:4px">${chips}</div>` : ''}
    </div>`
}

export function EVChargerLayer({
  location,
  visible = true,
  onStationSelect,
}: EVChargerLayerProps): null {
  const map = useMap()
  const initializedRef = useRef(false)
  const lastRadiusRef = useRef<number | null>(null)
  // Stable ref for the callback so the effect closure doesn't go stale.
  const onStationSelectRef = useRef(onStationSelect)
  onStationSelectRef.current = onStationSelect
  // F4/T-082: reassigned by each location effect so a persistent error toast's Retry
  // always refetches the CURRENT location instead of dead-no-opping on a stale controller.
  const retryRef = useRef<() => void>(() => {})

  // ── One-time source + layer setup ──────────────────────────────────────
  useEffect(() => {
    if (!map || initializedRef.current) return
    initializedRef.current = true
    // BEAD-0037: guards the async cluster-leaves continuation below. Set true on cleanup
    // (sign-out unmounts the layer) so a late-resolving getClusterLeaves promise does
    // not call map.easeTo() against a removed MapLibre instance.
    let removed = false

    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: true,
      // T-214: was 13 — bumped to the POI baseline so EV stations don't stop clustering
      // (and start cluttering) at a shallower zoom than the POIs.
      clusterMaxZoom: CLUSTER_MAX_ZOOM,
      clusterRadius: 50,
    })

    // T-214: register the 3 level-tinted charging-station chips (browser-only; a no-op in
    // jsdom/SSR). Icons attach to the symbol layer as they rasterize.
    void registerEvMarkerImages(map)

    // T-214: cluster chip — a brand-teal charging chip (the T-179 cluster look) instead of
    // the old plain teal bubble. Cluster MECHANICS are untouched (cluster:true, radii,
    // thresholds on the source) — this is a restyle only. Only features with a point_count.
    map.addLayer({
      id: LAYER_CLUSTER_ID,
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['has', 'point_count'],
      layout: {
        'icon-image': evClusterImageId(),
        'icon-size': 1,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    })

    // Cluster count — a top-right badge on the chip (mirrors the POI cluster count style)
    // rather than a number centered over the glyph.
    map.addLayer({
      id: LAYER_COUNT_ID,
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Noto Sans Regular'],
        'text-size': 12,
        'text-offset': [0.85, -0.85],
        'text-anchor': 'center',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': '#0f172a',
        'text-halo-width': 2,
      },
    })

    // Unclustered single stations — color-coded by highest level present.
    map.addLayer({
      id: LAYER_POINT_ID,
      type: 'circle',
      source: SOURCE_ID,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': [
          'case',
          ['>', ['get', 'portsDcFast'], 0],
          '#16a34a', // DC Fast — green
          ['>', ['get', 'portsLevel2'], 0],
          '#2563eb', // Level 2 — blue
          '#94a3b8', // Level 1 / unknown — slate
        ],
        'circle-radius': ['interpolate', ['linear'], ['get', 'totalPorts'], 1, 7, 10, 12, 50, 16],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': 0.9,
      },
    })

    // T-214: charging-station glyph chip on TOP of the circle (single stations only —
    // clusters get their own chip). Added after the circle so it renders above it.
    // `icon-image` is level-keyed so the chip tint matches the circle color.
    // Port-count size channel (amendment): the old dots encoded total ports via
    // circle-radius. A fixed chip would mask that (the chip covers small circles), so the
    // chip itself scales with totalPorts (icon-size mirrors the circle-radius breakpoints).
    // The circle is also kept beneath as a colored halo — belt and suspenders.
    map.addLayer({
      id: LAYER_ICON_ID,
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': evIconImageExpression() as unknown as string,
        'icon-size': ['interpolate', ['linear'], ['get', 'totalPorts'], 1, 0.85, 10, 1.05, 50, 1.3],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    })

    /**
     * T-435: this handler is bound to BOTH `ev-chargers-circle` and `ev-chargers-icon`, which draw
     * the same unclustered stations (same source, same `!has point_count` filter, the glyph chip
     * on top of the halo). MapLibre runs a layer-scoped handler for every layer under the cursor,
     * so a single station click has always fired this TWICE — and, when each call built its own
     * popup, opened two identical popups stacked on the marker. Routing through openMarkerPopup
     * makes the second call replace the first instead of piling onto it.
     */
    const onStationClick = (e: MapLayerMouseEvent) => {
      const f = e.features?.[0]
      if (!f?.properties) return
      openMarkerPopup(map, {
        lngLat: e.lngLat,
        maxWidth: '240px',
        offset: 10,
        html: stationPopupHTML(f.properties as Record<string, unknown>),
      })
      // Move the pin to the charger location so the side panel scores update.
      onStationSelectRef.current?.({ lat: e.lngLat.lat, lng: e.lngLat.lng })
    }

    /**
     * T-245 acceptance (5): a cluster click lists the stations inside it and moves the camera
     * in no way whatsoever — matching the POI layer. Clicking a decluttered chip means "what
     * is under here?", so answering with an unrequested zoom takes the map away from you when
     * the list is all you wanted. (Previously the zoom WAS the only action: this handler eased
     * to getClusterExpansionZoom and offered no list at all.) Zoom stays a map gesture.
     */
    const onClusterClick = (e: MapLayerMouseEvent) => {
      const f = e.features?.[0]
      if (!f) return
      const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number]
      const props = (f.properties ?? {}) as Record<string, unknown>
      const clusterId = props['cluster_id'] as number
      const pointCount = typeof props['point_count'] === 'number' ? props['point_count'] : undefined
      const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined
      if (!source) return
      void Promise.resolve(source.getClusterLeaves(clusterId, EV_CLUSTER_LEAVES_LIMIT, 0))
        .then((leaves) => {
          // BEAD-0037: layer unmounted mid-flight — do not touch a removed map.
          if (removed) return
          openStationLeavesPopup(
            map,
            coords,
            leaves as Array<{ properties?: Record<string, unknown> | null }>,
            pointCount,
          )
        })
        .catch(() => {
          /* cluster metadata unavailable — nothing to list */
        })
    }

    const onEnter = () => {
      map.getCanvas().style.cursor = 'pointer'
    }
    const onLeave = () => {
      map.getCanvas().style.cursor = ''
    }

    map.on('click', LAYER_POINT_ID, onStationClick)
    // T-214: the glyph chip can extend past the circle, so bind the same handlers to the
    // icon layer — clicking/hovering the glyph behaves exactly like the circle beneath.
    map.on('click', LAYER_ICON_ID, onStationClick)
    map.on('click', LAYER_CLUSTER_ID, onClusterClick)
    map.on('mouseenter', LAYER_POINT_ID, onEnter)
    map.on('mouseleave', LAYER_POINT_ID, onLeave)
    map.on('mouseenter', LAYER_ICON_ID, onEnter)
    map.on('mouseleave', LAYER_ICON_ID, onLeave)
    map.on('mouseenter', LAYER_CLUSTER_ID, onEnter)
    map.on('mouseleave', LAYER_CLUSTER_ID, onLeave)

    return () => {
      removed = true
      initializedRef.current = false
      // A popup outlives the layer that opened it — leaving one behind strands it over markers
      // that no longer exist.
      closeMarkerPopup(map)
      map.off('click', LAYER_POINT_ID, onStationClick)
      map.off('click', LAYER_ICON_ID, onStationClick)
      map.off('click', LAYER_CLUSTER_ID, onClusterClick)
      map.off('mouseenter', LAYER_POINT_ID, onEnter)
      map.off('mouseleave', LAYER_POINT_ID, onLeave)
      map.off('mouseenter', LAYER_ICON_ID, onEnter)
      map.off('mouseleave', LAYER_ICON_ID, onLeave)
      map.off('mouseenter', LAYER_CLUSTER_ID, onEnter)
      map.off('mouseleave', LAYER_CLUSTER_ID, onLeave)
      // BEAD-0037: skip layer/source teardown if MapView's map.remove() already ran on
      // sign-out (getLayer would throw on the dropped style and blank the app).
      if (isMapAlive(map)) {
        for (const id of ALL_LAYER_IDS) {
          if (map.getLayer(id)) map.removeLayer(id)
        }
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
        removeEvMarkerImages(map)
      }
    }
  }, [map])

  // ── Visibility toggle (mirrors TractLayer) ─────────────────────────────
  useEffect(() => {
    if (!map || !initializedRef.current) return
    const vis = visible ? 'visible' : 'none'
    for (const id of ALL_LAYER_IDS) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', vis)
    }
  }, [map, visible])

  // ── Fetch stations when location changes; re-fetch on zoom-tier change ──
  useEffect(() => {
    if (!map || !location) return
    const { lat, lng } = location
    // `let` + abort-then-reassign at the top of each fetch (mirrors POILayerMgr): every
    // fetch/retry gets a fresh controller, so a Retry is never a no-op on an aborted signal.
    let controller = new AbortController()

    const fetchStations = () => {
      controller.abort()
      controller = new AbortController()
      const { signal } = controller
      const radius = radiusMiForZoom(map.getZoom())
      lastRadiusRef.current = radius
      authedFetch(`/api/ev-stations/nearby?lat=${lat}&lng=${lng}&radius=${radius}`, {
        signal,
      })
        .then((r) => {
          // F8: skip if torn down / superseded mid-flight (matches County/Tract/Roads).
          if (signal.aborted) return null
          // A 504/5xx returns an HTML body that throws in .json() — check ok first so
          // the failure is a clear, user-visible toast instead of a swallowed parse error.
          if (!r.ok) throw new Error(`EV station fetch failed: ${r.status}`)
          return r.json()
        })
        .then((data: unknown) => {
          if (signal.aborted || !data) return
          const payload = data as { stations?: NormalizedStation[] }
          const fc = stationsToGeoJSON(payload.stations ?? [])
          const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined
          source?.setData(fc)
        })
        .catch((err: unknown) => {
          if ((err as Error)?.name === 'AbortError' || signal.aborted) return
          toast.error("Couldn't load nearby chargers.", {
            id: 'ev-fetch-error', // F7: dedupe — a flapping fetch updates one toast, no stacking
            duration: 8000, // F7: transient — auto-dismiss so it can't linger
            action: { label: 'Retry', onClick: () => retryRef.current() },
          })
        })
    }

    // F4: Retry targets the CURRENT location — retryRef is reassigned every effect run.
    retryRef.current = fetchStations
    fetchStations()

    // Re-fetch only when zoom crosses into a different radius tier.
    const onZoomEnd = () => {
      if (radiusMiForZoom(map.getZoom()) !== lastRadiusRef.current) fetchStations()
    }
    map.on('zoomend', onZoomEnd)

    return () => {
      controller.abort()
      map.off('zoomend', onZoomEnd)
    }
  }, [map, location])

  return null
}
